<template>
  <div>
    <v-card class="mt-4">
      <v-card-title>
        <v-icon class="mr-1" color="red" size="small">mdi-microphone</v-icon>
        Envoyer un nouveau message
      </v-card-title>
      <v-card-text>
        <p class="text-medium-emphasis text-body-2 mb-3">
          Cliquez sur le bouton ci-dessous pour commencer l'enregistrement d'un message. Pas de problème si vous vous trompez, vous pourrez ré-écouter votre message avant de l'envoyer et le ré-enregistrer si cela ne vous convient pas !
        </p>
        <the-message-recorder :device-id="deviceId" @update:recording="(v: boolean) => emit('update:recording', v)" @uploaded="messageUploaded" />
      </v-card-text>
    </v-card>

    <v-progress-circular v-if="loading" class="ma-4" color="primary" indeterminate />

    <template v-else>
      <!-- En attente de téléchargement -->
      <v-card v-if="pendingMessages.length > 0" class="mt-4">
        <v-card-title>
          <v-icon class="mr-1" color="warning" size="small">mdi-clock-outline</v-icon>
          Messages en attente
          <v-chip class="ml-2" density="compact" size="small" variant="tonal">{{ pendingMessages.length }}</v-chip>
        </v-card-title>
        <v-card-text>
          <p class="text-medium-emphasis text-body-2 mb-3">
            Ces messages ont été envoyés mais n'ont pas encore été téléchargés par le bouton. Il seront téléchargés lors de la prochaine synchronisation du bouton, dans quelques minutes.
          </p>
          <v-expansion-panels variant="accordion">
            <v-expansion-panel v-for="message in pendingMessages" :key="message.id">
              <v-expansion-panel-title>
                <div class="d-flex flex-wrap align-center w-100 ga-2 pr-2">
                  <span class="text-body-2">{{ formatDateShort(message.created_at) }}</span>
                  <v-chip v-if="message.to_delete" color="warning" size="x-small" variant="tonal">
                    En cours de suppression
                  </v-chip>
                  <v-chip v-else-if="message.is_mine" color="secondary" size="x-small" variant="tonal">
                    Envoyé par vous
                  </v-chip>

                  <v-spacer class="d-none d-sm-block" />

                  <div class="w-100 w-sm-auto d-flex align-center justify-end mt-2 mt-sm-0 ga-2">
                    <the-message-player-compact v-if="message.is_mine" :message-id="message.id" />
                    <v-chip :color="storageColor(message.used_storage_percent)" density="compact" size="x-small" variant="tonal">
                      <v-icon size="x-small" start>mdi-harddisk</v-icon>
                      {{ message.used_storage_percent.toFixed(1) }}%
                    </v-chip>
                    <v-btn
                      v-if="message.is_mine"
                      color="error"
                      :disabled="message.to_delete"
                      icon="mdi-delete"
                      size="x-small"
                      variant="text"
                      @click.stop="confirmDelete(message)"
                    />
                  </div>
                </div>
              </v-expansion-panel-title>
              <v-expansion-panel-text>
                <the-message-details :msg="message" />
              </v-expansion-panel-text>
            </v-expansion-panel>
          </v-expansion-panels>
        </v-card-text>
      </v-card>

      <!-- Téléchargés, en attente d'écoute -->
      <v-card v-if="downloadedMessages.length > 0" class="mt-4">
        <v-card-title>
          <v-icon class="mr-1" color="info" size="small">mdi-download</v-icon>
          Messages à écouter
          <v-chip class="ml-2" density="compact" size="small" variant="tonal">{{ downloadedMessages.length }}</v-chip>
        </v-card-title>
        <v-card-text>
          <p class="text-medium-emphasis text-body-2 mb-3">
            Ces messages ont été téléchargés par le bouton et attendent d'être écoutés.
          </p>
          <v-expansion-panels variant="accordion">
            <v-expansion-panel v-for="message in downloadedMessages" :key="message.id">
              <v-expansion-panel-title>
                <div class="d-flex flex-wrap align-center w-100 ga-2 pr-2">
                  <span class="text-body-2">{{ formatDateShort(message.created_at) }}</span>
                  <v-chip v-if="message.to_delete" color="warning" size="x-small" variant="tonal">
                    En cours de suppression
                  </v-chip>
                  <v-chip v-else-if="message.is_mine" color="secondary" size="x-small" variant="tonal">
                    Envoyé par vous
                  </v-chip>

                  <v-spacer class="d-none d-sm-block" />

                  <div class="w-100 w-sm-auto d-flex align-center justify-end mt-2 mt-sm-0 ga-2">
                    <the-message-player-compact v-if="message.is_mine" :message-id="message.id" />
                    <v-chip :color="storageColor(message.used_storage_percent)" density="compact" size="x-small" variant="tonal">
                      <v-icon size="x-small" start>mdi-harddisk</v-icon>
                      {{ message.used_storage_percent.toFixed(1) }}%
                    </v-chip>
                    <v-btn
                      v-if="message.is_mine"
                      color="error"
                      :disabled="message.to_delete"
                      icon="mdi-delete"
                      size="x-small"
                      variant="text"
                      @click.stop="confirmDelete(message)"
                    />
                  </div>
                </div>
              </v-expansion-panel-title>
              <v-expansion-panel-text>
                <the-message-details :msg="message" />
              </v-expansion-panel-text>
            </v-expansion-panel>
          </v-expansion-panels>
        </v-card-text>
      </v-card>

      <!-- Écoutés -->
      <v-card v-if="listenedMessages.length > 0" class="mt-4">
        <v-card-title>
          <v-icon class="mr-1" color="success" size="small">mdi-check-circle</v-icon>
          Messages écoutés
          <v-chip class="ml-2" density="compact" size="small" variant="tonal">{{ listenedMessages.length }}</v-chip>
        </v-card-title>
        <v-card-text>
          <p class="text-medium-emphasis text-body-2 mb-3">
            Ces messages ont déjà été écoutés au moins une fois.
          </p>
          <v-expansion-panels variant="accordion">
            <v-expansion-panel v-for="message in listenedMessages" :key="message.id">
              <v-expansion-panel-title>
                <div class="d-flex flex-wrap align-center w-100 ga-2 pr-2">
                  <span class="text-body-2">{{ formatDateShort(message.created_at) }}</span>
                  <v-chip v-if="message.to_delete" color="warning" size="x-small" variant="tonal">
                    En cours de suppression
                  </v-chip>
                  <v-chip v-else-if="message.is_mine" color="secondary" size="x-small" variant="tonal">
                    Envoyé par vous
                  </v-chip>

                  <v-spacer class="d-none d-sm-block" />

                  <div class="w-100 w-sm-auto d-flex align-center justify-end mt-2 mt-sm-0 ga-2">
                    <the-message-player-compact v-if="message.is_mine" :message-id="message.id" />
                    <v-chip :color="storageColor(message.used_storage_percent)" density="compact" size="x-small" variant="tonal">
                      <v-icon size="x-small" start>mdi-harddisk</v-icon>
                      {{ message.used_storage_percent.toFixed(1) }}%
                    </v-chip>
                    <span class="text-caption text-medium-emphasis">
                      {{ message.play_count ?? 0 }} écoute{{ (message.play_count ?? 0) > 1 ? 's' : '' }}
                    </span>
                    <v-btn
                      v-if="message.is_mine"
                      color="error"
                      :disabled="message.to_delete"
                      icon="mdi-delete"
                      size="x-small"
                      variant="text"
                      @click.stop="confirmDelete(message)"
                    />
                  </div>
                </div>
              </v-expansion-panel-title>
              <v-expansion-panel-text>
                <the-message-details :msg="message" />
              </v-expansion-panel-text>
            </v-expansion-panel>
          </v-expansion-panels>
        </v-card-text>
      </v-card>
    </template>

    <!-- Modal de confirmation de suppression -->
    <v-dialog v-model="showDeleteDialog" max-width="400">
      <v-card>
        <v-card-title>Supprimer le message ?</v-card-title>
        <v-card-text>
          Ce message sera supprimé du bouton lors de sa prochaine synchronisation.
          Cette action est irréversible.
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn :disabled="deleting" variant="text" @click="showDeleteDialog = false">Annuler</v-btn>
          <v-btn
            color="error"
            :loading="deleting"
            prepend-icon="mdi-delete"
            variant="flat"
            @click="deleteMessage"
          >
            Supprimer
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script setup lang="ts">
  import type { Message } from '@/model/message.ts'
  import { computed, onBeforeUnmount, onMounted, ref } from 'vue'
  import { deviceRepository } from '@/api/repository/device.repository.ts'
  import TheMessageDetails from '@/components/device-components/TheMessageDetails.vue'
  import TheMessagePlayerCompact from '@/components/device-components/TheMessagePlayerCompact.vue'
  import TheMessageRecorder from '@/components/device-components/TheMessageRecorder.vue'
  import { ToastType, useToastStore } from '@/stores/toast.store.ts'
  import { notifyApiError } from '@/utils/error.utils.ts'

  const props = defineProps<{
    deviceId: string
  }>()

  const emit = defineEmits<{
    'update:recording': [value: boolean]
  }>()

  const messages = ref<Message[]>([])
  const loading = ref(true)

  function sortByNewest (list: Message[]): Message[] {
    return list.toSorted((a, b) => b.created_at.getTime() - a.created_at.getTime())
  }

  const pendingMessages = computed(() =>
    sortByNewest(messages.value.filter(m => !m.downloaded_at)),
  )

  const downloadedMessages = computed(() =>
    sortByNewest(messages.value.filter(m => m.downloaded_at && !m.first_played_at)),
  )

  const listenedMessages = computed(() =>
    sortByNewest(messages.value.filter(m => !!m.first_played_at)),
  )

  async function loadMessages () {
    loading.value = true
    try {
      messages.value = await deviceRepository.listMessages(props.deviceId)
    } catch (error) {
      notifyApiError('Impossible de charger la liste des messages', error)
      console.error('Failed to load messages', error)
    } finally {
      loading.value = false
    }
  }

  function formatDateShort (date: Date): string {
    return date.toLocaleString('fr-FR', {
      dateStyle: 'short',
      timeStyle: 'short',
    })
  }

  function storageColor (percent: number): string {
    if (percent >= 10) return 'error'
    if (percent >= 5) return 'warning'
    return 'info'
  }

  function messageUploaded () {
    loadMessages()
    useToastStore().notify(
      'Message envoyé avec succès',
      ToastType.Success,
    )
  }

  async function refreshMessages () {
    try {
      messages.value = await deviceRepository.listMessages(props.deviceId)
    } catch {
      // silently ignore refresh errors
    }
  }

  const RELOAD_INTERVAL_MS = 120_000
  let reloadInterval: ReturnType<typeof setInterval> | null = null

  onMounted(() => {
    loadMessages()
    reloadInterval = setInterval(refreshMessages, RELOAD_INTERVAL_MS)
  })

  onBeforeUnmount(() => {
    if (reloadInterval) {
      clearInterval(reloadInterval)
    }
  })

  const showDeleteDialog = ref(false)
  const messageToDelete = ref<Message | null>(null)
  const deleting = ref(false)

  function confirmDelete (message: Message) {
    messageToDelete.value = message
    showDeleteDialog.value = true
  }

  async function deleteMessage () {
    if (!messageToDelete.value) return
    deleting.value = true
    try {
      await deviceRepository.deleteMessage(messageToDelete.value.id)
      useToastStore().notify('Message marqué pour suppression', ToastType.Success)
      showDeleteDialog.value = false
      messageToDelete.value = null
      await loadMessages()
    } catch (error) {
      notifyApiError('Erreur lors de la suppression du message', error)
    } finally {
      deleting.value = false
    }
  }

  defineExpose({ refreshMessages })
</script>

<style scoped>
:deep(.v-card-title) {
  white-space: normal !important;
  overflow: visible !important;
  text-overflow: unset !important;
}
</style>
